import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Input } from '@/components';
import { apiJson, ApiError } from '@/lib/api';

type FeatureFlagMode = 'disabled' | 'enabled' | 'whitelist';

type FeatureFlagMember = {
  user_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
};

type FeatureFlagListItem = {
  id: number;
  key: string;
  description: string;
  mode: FeatureFlagMode;
  whitelist_count: number;
  created_at: string;
  updated_at: string;
};

type FeatureFlagWhitelistHousehold = {
  household_id: number;
  members: FeatureFlagMember[];
  created_at: string | null;
};

type FeatureFlagDetail = {
  id: number;
  key: string;
  description: string;
  mode: FeatureFlagMode;
  whitelist_count: number;
  whitelist: FeatureFlagWhitelistHousehold[];
  created_at: string;
  updated_at: string;
};

type HouseholdSearchResult = {
  household_id: number;
  members: FeatureFlagMember[];
  already_whitelisted: boolean;
};

type HouseholdSearchResponse = {
  households: HouseholdSearchResult[];
};

const MODE_LABELS: Record<FeatureFlagMode, string> = {
  disabled: 'Disabled',
  enabled: 'Enabled for all',
  whitelist: 'Whitelist',
};

const MODE_BADGE_CLASS: Record<FeatureFlagMode, string> = {
  disabled: 'bg-nova-muted text-nova-foreground',
  enabled: 'bg-green-50 text-green-800',
  whitelist: 'bg-amber-50 text-amber-800',
};

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: string | string[] };
      if (typeof j.detail === 'string') return j.detail;
      if (Array.isArray(j.detail)) return j.detail.map(String).join('; ');
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

function formatAdminDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function memberDisplayName(m: FeatureFlagMember): string {
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ').trim();
  return name || m.email || m.phone_number || `User ${m.user_id}`;
}

function membersSummary(members: FeatureFlagMember[]): string {
  if (members.length === 0) return 'No members';
  return members.map(memberDisplayName).join(' · ');
}

/** Same phone-paste normalization as admin users search. */
function normalizeAdminSearchQuery(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (trimmed.includes('@')) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length >= 7 && !/[a-zA-Z]/.test(trimmed)) {
    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  }
  return trimmed;
}

export const Route = createFileRoute('/admin/feature-flags')({
  component: FeatureFlagsPage,
});

function FeatureFlagsPage() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createKey, setCreateKey] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['admin', 'feature-flags'],
    queryFn: () =>
      apiJson<FeatureFlagListItem[]>(getToken, '/v1/admin/feature-flags'),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      apiJson<FeatureFlagDetail>(getToken, '/v1/admin/feature-flags', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: createKey.trim(),
          description: createDescription.trim(),
          mode: 'disabled',
        }),
      }),
    onSuccess: (created) => {
      setCreateError(null);
      setCreateKey('');
      setCreateDescription('');
      void qc.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      setSelectedId(created.id);
    },
    onError: (err) => setCreateError(parseDetail(err)),
  });

  if (listQuery.isLoading) {
    return <div className="py-12 text-center text-sm text-nova-muted-foreground">Loading…</div>;
  }

  if (listQuery.error) {
    return (
      <p className="text-sm text-red-600">
        {parseDetail(listQuery.error)}
        <button
          type="button"
          className="ml-2 underline"
          onClick={() => void listQuery.refetch()}
        >
          Retry
        </button>
      </p>
    );
  }

  const flags = listQuery.data ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-nova-foreground">Feature flags</h1>
        <p className="mt-1 text-sm text-nova-muted-foreground">
          Household-scoped flags resolved by{' '}
          <code className="text-xs">GET /v1/feature-flags</code>. Modes: disabled,
          enabled for all, or household whitelist.
        </p>
      </div>

      <Card className="p-4">
        <h2 className="mb-3 text-lg font-medium text-nova-foreground">Create flag</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Key"
            placeholder="e.g. mediated_chat"
            value={createKey}
            onChange={(e) => setCreateKey(e.target.value)}
          />
          <Input
            label="Description"
            placeholder="What this gates"
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
          />
        </div>
        {createError ? (
          <p className="mt-2 text-sm text-red-600">{createError}</p>
        ) : null}
        <div className="mt-3">
          <Button
            onClick={() => void createMutation.mutate()}
            disabled={!createKey.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden p-0">
        <div className="border-b border-nova-border px-4 py-3">
          <h2 className="text-lg font-medium text-nova-foreground">Flags</h2>
        </div>
        {flags.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-nova-muted-foreground">
            No feature flags yet.
          </p>
        ) : (
          <ul className="divide-y divide-nova-border">
            {flags.map((flag) => (
              <li key={flag.id}>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedId((prev) => (prev === flag.id ? null : flag.id))
                  }
                  className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-nova-accent ${
                    selectedId === flag.id ? 'bg-nova-accent' : ''
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-nova-foreground">
                        {flag.key}
                      </span>
                      <span
                        className={`rounded-md px-2 py-0.5 text-xs font-medium ${MODE_BADGE_CLASS[flag.mode]}`}
                      >
                        {MODE_LABELS[flag.mode]}
                      </span>
                      {flag.mode === 'whitelist' ? (
                        <span className="text-xs text-nova-muted-foreground">
                          {flag.whitelist_count} household
                          {flag.whitelist_count === 1 ? '' : 's'}
                        </span>
                      ) : null}
                    </div>
                    {flag.description ? (
                      <p className="mt-0.5 truncate text-sm text-nova-muted-foreground">
                        {flag.description}
                      </p>
                    ) : null}
                  </div>
                  <span className="shrink-0 text-xs text-nova-muted-foreground">
                    {formatAdminDateTime(flag.updated_at)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {selectedId != null ? (
        <FlagDetailPanel
          flagId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            void qc.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
          }}
        />
      ) : null}

      <Button variant="secondary" onClick={() => void listQuery.refetch()}>
        Refresh
      </Button>
    </div>
  );
}

function FlagDetailPanel({
  flagId,
  onClose,
  onChanged,
}: {
  flagId: number;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { getToken } = useAuth();
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<FeatureFlagMode>('disabled');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [searchQ, setSearchQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQ(normalizeAdminSearchQuery(searchQ));
    }, 250);
    return () => window.clearTimeout(t);
  }, [searchQ]);

  const detailQuery = useQuery({
    queryKey: ['admin', 'feature-flags', flagId],
    queryFn: () =>
      apiJson<FeatureFlagDetail>(getToken, `/v1/admin/feature-flags/${flagId}`),
  });

  useEffect(() => {
    if (!detailQuery.data) return;
    setDescription(detailQuery.data.description);
    setMode(detailQuery.data.mode);
  }, [detailQuery.data]);

  const searchQuery = useQuery({
    queryKey: ['admin', 'feature-flags', 'household-search', flagId, debouncedQ],
    queryFn: () => {
      const params = new URLSearchParams({
        q: debouncedQ,
        flag_id: String(flagId),
        limit: '20',
      });
      return apiJson<HouseholdSearchResponse>(
        getToken,
        `/v1/admin/feature-flags/households/search?${params.toString()}`,
      );
    },
    enabled: debouncedQ.length >= 1,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const current = detailQuery.data;
      if (!current) throw new Error('Flag not loaded');
      if (mode !== current.mode) {
        const confirmMsg =
          mode === 'enabled'
            ? `Enable "${current.key}" for ALL households?`
            : current.mode === 'enabled'
              ? `Turn off "${current.key}" for households that currently have it via enabled-for-all?`
              : null;
        if (confirmMsg && !window.confirm(confirmMsg)) {
          throw new Error('cancelled');
        }
      }
      return apiJson<FeatureFlagDetail>(
        getToken,
        `/v1/admin/feature-flags/${flagId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ description, mode }),
        },
      );
    },
    onSuccess: () => {
      setSaveError(null);
      void detailQuery.refetch();
      onChanged();
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'cancelled') return;
      setSaveError(parseDetail(err));
    },
  });

  const addMutation = useMutation({
    mutationFn: (householdId: number) =>
      apiJson<FeatureFlagDetail>(
        getToken,
        `/v1/admin/feature-flags/${flagId}/whitelist`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ household_id: householdId }),
        },
      ),
    onSuccess: () => {
      void detailQuery.refetch();
      void searchQuery.refetch();
      onChanged();
    },
  });

  const removeMutation = useMutation({
    mutationFn: (householdId: number) =>
      apiJson<FeatureFlagDetail>(
        getToken,
        `/v1/admin/feature-flags/${flagId}/whitelist/${householdId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void detailQuery.refetch();
      void searchQuery.refetch();
      onChanged();
    },
  });

  const dirty = useMemo(() => {
    if (!detailQuery.data) return false;
    return (
      description !== detailQuery.data.description ||
      mode !== detailQuery.data.mode
    );
  }, [description, detailQuery.data, mode]);

  if (detailQuery.isLoading) {
    return (
      <Card className="p-4">
        <p className="text-sm text-nova-muted-foreground">Loading flag…</p>
      </Card>
    );
  }

  if (detailQuery.error || !detailQuery.data) {
    return (
      <Card className="p-4">
        <p className="text-sm text-red-600">
          {parseDetail(detailQuery.error ?? new Error('Not found'))}
        </p>
        <Button variant="secondary" className="mt-3" onClick={onClose}>
          Close
        </Button>
      </Card>
    );
  }

  const flag = detailQuery.data;

  return (
    <Card className="space-y-5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-mono text-lg font-semibold text-nova-foreground">
            {flag.key}
          </h2>
          <p className="text-xs text-nova-muted-foreground">
            Updated {formatAdminDateTime(flag.updated_at)}
          </p>
        </div>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>

      <Input
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <fieldset>
        <legend className="mb-2 text-sm font-medium text-nova-foreground">Mode</legend>
        <div className="space-y-2">
          {(Object.keys(MODE_LABELS) as FeatureFlagMode[]).map((value) => (
            <label key={value} className="flex items-center gap-2 text-sm text-nova-foreground">
              <input
                type="radio"
                name={`flag-mode-${flag.id}`}
                checked={mode === value}
                onChange={() => setMode(value)}
              />
              {MODE_LABELS[value]}
            </label>
          ))}
        </div>
      </fieldset>

      {saveError ? <p className="text-sm text-red-600">{saveError}</p> : null}

      <Button
        onClick={() => void saveMutation.mutate()}
        disabled={!dirty || saveMutation.isPending}
      >
        {saveMutation.isPending ? 'Saving…' : 'Save changes'}
      </Button>

      {(mode === 'whitelist' || flag.mode === 'whitelist') && (
        <div className="space-y-4 border-t border-nova-border pt-5">
          <div>
            <h3 className="text-base font-medium text-nova-foreground">
              Household whitelist
            </h3>
            <p className="mt-0.5 text-sm text-nova-muted-foreground">
              Search by any member&apos;s name, email, or phone.
            </p>
          </div>

          <Input
            label="Search households"
            placeholder="Name, email, or phone"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
          />

          {debouncedQ ? (
            <div className="rounded-md border border-nova-border">
              {searchQuery.isFetching ? (
                <p className="px-3 py-3 text-sm text-nova-muted-foreground">Searching…</p>
              ) : (searchQuery.data?.households ?? []).length === 0 ? (
                <p className="px-3 py-3 text-sm text-nova-muted-foreground">No households found.</p>
              ) : (
                <ul className="divide-y divide-nova-border">
                  {(searchQuery.data?.households ?? []).map((hh) => (
                    <li
                      key={hh.household_id}
                      className="flex items-start justify-between gap-3 px-3 py-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-nova-foreground">
                          Household #{hh.household_id}
                        </p>
                        <p className="text-xs text-nova-muted-foreground">
                          {membersSummary(hh.members)}
                        </p>
                        <ul className="mt-1 space-y-0.5 text-xs text-nova-muted-foreground">
                          {hh.members.map((m) => (
                            <li key={m.user_id}>
                              {memberDisplayName(m)}
                              {m.email ? ` · ${m.email}` : ''}
                              {m.phone_number ? ` · ${m.phone_number}` : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {hh.already_whitelisted ? (
                        <span className="shrink-0 text-xs font-medium text-nova-muted-foreground">
                          Already added
                        </span>
                      ) : (
                        <Button
                          onClick={() => void addMutation.mutate(hh.household_id)}
                          disabled={addMutation.isPending}
                        >
                          Add
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          <div>
            <h4 className="mb-2 text-sm font-medium text-nova-foreground">
              Current whitelist ({flag.whitelist.length})
            </h4>
            {flag.whitelist.length === 0 ? (
              <p className="text-sm text-nova-muted-foreground">No households whitelisted yet.</p>
            ) : (
              <ul className="divide-y divide-nova-border rounded-md border border-nova-border">
                {flag.whitelist.map((hh) => (
                  <li
                    key={hh.household_id}
                    className="flex items-start justify-between gap-3 px-3 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-nova-foreground">
                        Household #{hh.household_id}
                      </p>
                      <ul className="mt-1 space-y-0.5 text-xs text-nova-muted-foreground">
                        {hh.members.map((m) => (
                          <li key={m.user_id}>
                            {memberDisplayName(m)}
                            {m.email ? ` · ${m.email}` : ''}
                            {m.phone_number ? ` · ${m.phone_number}` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove household #${hh.household_id} from the whitelist?`,
                          )
                        ) {
                          void removeMutation.mutate(hh.household_id);
                        }
                      }}
                      disabled={removeMutation.isPending}
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
